import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/options";
import { RcButton } from "@/components/RcButton";

const STATUS_LABEL = {
  draft: "Draft",
  ordered: "Ordered",
  paid: "Paid",
  in_production: "In Production",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export default function MyDesigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/designs");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load designs");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this design?")) return;
    try {
      await api.delete(`/designs/${id}`);
      setItems((prev) => prev.filter((d) => d.id !== id));
      toast.success("Design removed");
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="p-16 text-center text-[#A39E93]">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-2">Wardrobe</div>
          <h1 className="font-display text-5xl tracking-tight">My Designs</h1>
        </div>
        <Link to="/designer"><RcButton variant="outline" data-testid="new-design-btn">+ New Design</RcButton></Link>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-[#362B28] rounded-sm">
          <div className="font-display text-3xl mb-3">Nothing here yet.</div>
          <p className="text-[#A39E93] mb-6 font-light">Compose your first garment in the configurator.</p>
          <Link to="/designer"><RcButton>Start Designing</RcButton></Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((d) => (
            <div key={d.id} data-testid={`design-card-${d.id}`} className="border border-[#362B28] bg-[#1F1816] rounded-sm overflow-hidden group">
              <div className="aspect-[4/5] bg-[#120D0C] relative overflow-hidden">
                {d.preview_image ? (
                  <img src={d.preview_image} alt={d.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full" style={{ background: d.color }} />
                  </div>
                )}
                <div className="absolute top-3 right-3 px-2 py-1 text-[10px] tracking-[0.2em] uppercase bg-[#120D0C]/80 border border-[#362B28] text-[#CFA05E] backdrop-blur">
                  {STATUS_LABEL[d.status] || d.status}
                </div>
              </div>
              <div className="p-5">
                <div className="font-display text-2xl mb-1">{d.name}</div>
                <div className="text-xs text-[#A39E93] tracking-wider uppercase mb-4">
                  {d.dress_type.replace("_"," ")} · {d.fabric}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[#CFA05E] font-display text-2xl">{formatINR(d.price)}</div>
                  <button
                    onClick={() => remove(d.id)}
                    data-testid={`delete-${d.id}`}
                    className="text-xs tracking-[0.2em] uppercase text-[#A39E93] hover:text-[#6B2737] transition"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
